import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import DOMPurify from 'dompurify';
import './FeedbackPopup.css';
import { API_ROUTE, Routes } from '../helper/Routes';
import { PopupProps } from '../helper/interface';

const encodeHtml = (str: string): string =>
  str.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');

interface FeedbackPopupProps extends PopupProps {
  userName?: string;
  userEmail?: string;
  contextLabel?: string;
}

interface CheckboxState {
  lookAndFeel: boolean;
  spelling: boolean;
  usability: boolean;
  navigation: boolean;
  cardContent: boolean;
}

interface User {
  name: string;
  email: string;
}

interface Page {
  name: string;
  url: string;
  path: string;
}

const PLACEHOLDER_HTML = '<p style="color: #94a3b8">Type your message here</p>';

const INITIAL_CHECKBOX_STATE: CheckboxState = {
  lookAndFeel: false,
  spelling: false,
  usability: false,
  navigation: false,
  cardContent: false,
};

const CHECKBOX_ITEMS: Array<{ key: keyof CheckboxState; label: string }> = [
  { key: 'lookAndFeel', label: 'Look and Feel' },
  { key: 'spelling', label: 'Spelling' },
  { key: 'usability', label: 'Usability' },
  { key: 'navigation', label: 'Navigation' },
  { key: 'cardContent', label: 'Card Content' },
];

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({
  show,
  onClose,
  userName = 'John Doe',
  userEmail = 'john.doe@example.com',
  contextLabel,
}) => {
  const [checkboxes, setCheckboxes] = useState<CheckboxState>(INITIAL_CHECKBOX_STATE);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const resetForm = () => {
    setCheckboxes(INITIAL_CHECKBOX_STATE);
    setIsSubmitting(false);
    setSubmitSuccess(false);

    if (editorRef.current) {
      editorRef.current.innerHTML = PLACEHOLDER_HTML;
    }
  };

  useEffect(() => {
    if (!show) {
      const timer = setTimeout(() => {
        resetForm();
      }, 240);

      return () => clearTimeout(timer);
    }

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [show]);

  const currentUser: User = {
    name: userName,
    email: userEmail,
  };

  const getCurrentPage = (): Page => {
    const url = window.location.href;
    const path = window.location.pathname;
    const pageName = path === '/'
      ? 'Home'
      : path.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') || 'Unknown Page';

    return {
      name: pageName.charAt(0).toUpperCase() + pageName.slice(1),
      url,
      path,
    };
  };

  const handleCheckboxChange = (key: keyof CheckboxState): void => {
    setCheckboxes((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const applyFormat = (command: string, value: string | null = null): void => {
    document.execCommand(command, false, value || undefined);
    editorRef.current?.focus();
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);

    const currentPage = getCurrentPage();

    const selectedCategories = (Object.keys(checkboxes) as Array<keyof CheckboxState>)
      .filter((key) => checkboxes[key])
      .map((key) => key.replace(/([A-Z])/g, ' $1').trim())
      .map((value) => value.charAt(0).toUpperCase() + value.slice(1));

    const messageContent = editorRef.current?.innerHTML || '';
    const hasRealContent = !messageContent.includes('Type your message here');
    const finalMessage = hasRealContent
      ? DOMPurify.sanitize(messageContent, {
          ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'ul', 'ol', 'li'],
          ALLOWED_ATTR: [],
        })
      : '<p>No message provided</p>';

    const payload = {
      message: {
        subject: contextLabel
          ? `Feedback from ${currentPage.name} (${contextLabel})`
          : `Feedback from ${currentPage.name}`,
        body: {
          contentType: 'HTML',
          content: `
            <!DOCTYPE html>
            <html>
            <head>
             <style>
                body {
                  font-family: Arial, sans-serif;
                  line-height: 1.6;
                  color: #333;
                }

                .container {
                  max-width: 600px;
                  margin: 0 auto;
                  padding: 20px;
                }

                .header {
                  background-color: #2d5f2e;
                  color: #ffffff;
                  padding: 20px;
                  border-radius: 8px 8px 0 0;
                }

                .content {
                  background-color: #fefbe9;
                  padding: 20px;
                  border: 1px solid #e0e0e0;
                }

                .section {
                  margin-bottom: 20px;
                }

                .section-title {
                  font-size: 16px;
                  font-weight: bold;
                  color: #2d5f2e;
                  margin-bottom: 10px;
                  border-bottom: 2px solid #2d5f2e;
                  padding-bottom: 5px;
                }

                .info-row {
                  margin: 8px 0;
                }

                .label {
                  font-weight: bold;
                  color: #666;
                }

                .message-box {
                  background: #ffffff;
                  padding: 15px;
                  border-radius: 8px;
                  border: 1px solid #ddd;
                  margin-top: 10px;
                }

                .categories {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 8px;
                }

                .category-tag {
                  background-color: #D0FAE5;
                  color: #2d5f2e;
                  padding: 6px 12px;
                  border-radius: 16px;
                  font-size: 14px;
                  font-weight: 500;
                }

                .footer {
                  background-color: #e8e8e8;
                  padding: 15px;
                  text-align: center;
                  color: #666;
                  font-size: 12px;
                  border-radius: 0 0 8px 8px;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="header">
                  <h2 style="margin: 0;">New Feedback Submission</h2>
                </div>
                <div class="content">
                  <div class="section">
                    <div class="section-title">Feedback Categories</div>
                    ${selectedCategories.length > 0
                      ? `<div class="categories">${selectedCategories.map((category) => `<span class="category-tag">${category}</span>`).join('')}</div>`
                      : '<p style="color: #9ca3af;">No categories selected</p>'
                    }
                  </div>
                  <div class="section">
                    <div class="section-title">Message</div>
                    <div class="message-box">
                      ${finalMessage}
                    </div>
                  </div>
                  <div class="section">
                    <div class="section-title">Submitted By</div>
                    <div class="info-row">
                      <span class="label">Name:</span> ${encodeHtml(currentUser.name)}
                    </div>
                    <div class="info-row">
                      <span class="label">Email:</span> <a href="mailto:${encodeHtml(currentUser.email)}">${encodeHtml(currentUser.email)}</a>
                    </div>
                    <div class="info-row">
                      <span class="label">From:</span> ${encodeHtml(currentUser.name)} &lt;${encodeHtml(currentUser.email)}&gt;
                    </div>
                  </div>
                  <div class="section">
                    <div class="section-title">Page Information</div>
                    <div class="info-row">
                      <span class="label">Page Name:</span> ${encodeHtml(currentPage.name)}
                    </div>
                    ${contextLabel
                      ? `<div class="info-row">
                      <span class="label">Card Template:</span> ${encodeHtml(contextLabel)}
                    </div>`
                      : ''
                    }
                    <div class="info-row">
                      <span class="label">Full URL:</span> <a href="${encodeHtml(currentPage.url)}">${encodeHtml(currentPage.url)}</a>
                    </div>
                  </div>
                </div>
                <div class="footer">
                  <p style="margin: 0;">Submitted on: ${new Date().toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}</p>
                </div>
              </div>
            </body>
            </html>
          `,
        },
      },
    };

    try {
      const response = await Axios.post(API_ROUTE + Routes.SEND_EMAIL, payload);
      console.log('Email sent successfully:', response.data);

      setIsSubmitting(false);
      setSubmitSuccess(true);

      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      setIsSubmitting(false);
      alert('Failed to send feedback. Please try again.');
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!show) {
    return null;
  }

  const currentPage = getCurrentPage();

  return (
    <div className="feedback-popup-overlay">
      <div
        className="feedback-popup-card"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-popup-title"
      >
        <button
          type="button"
          onClick={handleClose}
          className="feedback-popup-close"
          aria-label="Close feedback popup"
        >
          ×
        </button>

        <div className="feedback-popup-header">
          <span className="feedback-popup-kicker">Feedback</span>
          <h2 id="feedback-popup-title" className="feedback-popup-title">Share feedback</h2>
          <p className="feedback-popup-subtitle">
            Tell us what needs attention on
            {' '}
            <strong>{currentPage.name}</strong>
            .
          </p>
        </div>

        {submitSuccess ? (
          <div className="feedback-popup-success">
            <div className="feedback-popup-success-icon">✓</div>
            <h3>Thank you</h3>
            <p>Your feedback has been submitted successfully.</p>
          </div>
        ) : (
          <>
            <div className="feedback-popup-badges">
              <span className="feedback-popup-badge feedback-popup-badge--neutral">Current page</span>
              <span className="feedback-popup-badge feedback-popup-badge--info">{currentPage.name}</span>
              {contextLabel && (
                <span className="feedback-popup-badge feedback-popup-badge--info">{contextLabel}</span>
              )}
            </div>

            <div className="feedback-popup-grid">
              <section className="feedback-popup-section feedback-popup-section--topics">
                <div className="feedback-popup-section-head">
                  <h3 className="feedback-popup-section-title">What should we look at?</h3>
                  <p className="feedback-popup-section-subtitle">Choose any areas that need review.</p>
                </div>

                <div className="feedback-popup-checkboxes">
                  {CHECKBOX_ITEMS.map(({ key, label }) => (
                    <label key={key} htmlFor={key} className={`feedback-popup-checkbox-card${checkboxes[key] ? ' is-selected' : ''}`}>
                      <input
                        type="checkbox"
                        id={key}
                        checked={checkboxes[key]}
                        onChange={() => handleCheckboxChange(key)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </section>

              <section className="feedback-popup-section feedback-popup-section--message">
                <div className="feedback-popup-section-head">
                  <label className="feedback-popup-section-title" htmlFor="feedback-editor">Message</label>
                  <p className="feedback-popup-section-subtitle">Add context, examples, or steps to reproduce.</p>
                </div>

                <div className="feedback-popup-editor-shell">
                  <div className="feedback-popup-toolbar">
                    <button
                      type="button"
                      onClick={() => applyFormat('bold')}
                      className="feedback-popup-toolbar-btn feedback-popup-toolbar-btn--strong"
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('italic')}
                      className="feedback-popup-toolbar-btn feedback-popup-toolbar-btn--emphasis"
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('underline')}
                      className="feedback-popup-toolbar-btn feedback-popup-toolbar-btn--underline"
                      title="Underline"
                    >
                      U
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('insertUnorderedList')}
                      className="feedback-popup-toolbar-btn"
                      title="Bullet list"
                    >
                      • List
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('insertOrderedList')}
                      className="feedback-popup-toolbar-btn"
                      title="Numbered list"
                    >
                      1. List
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter URL:');
                        if (url) {
                          applyFormat('createLink', url);
                        }
                      }}
                      className="feedback-popup-toolbar-btn"
                      title="Insert link"
                    >
                      Link
                    </button>
                  </div>

                  <div
                    id="feedback-editor"
                    ref={editorRef}
                    contentEditable
                    className="feedback-popup-editor"
                    suppressContentEditableWarning
                    onInput={() => { /* content read via ref on submit */ }}
                    onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
                      if (event.currentTarget.innerHTML.includes('Type your message here')) {
                        event.currentTarget.innerHTML = '';
                      }
                    }}
                    onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
                      if (event.currentTarget.innerHTML.trim() === '') {
                        event.currentTarget.innerHTML = PLACEHOLDER_HTML;
                      }
                    }}
                  >
                    <p style={{ color: '#94a3b8' }}>Type your message here</p>
                  </div>
                </div>
              </section>
            </div>

            <div className="feedback-popup-actions">
              <button
                type="button"
                className="feedback-popup-action feedback-popup-action--cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="feedback-popup-action feedback-popup-action--submit"
                disabled={isSubmitting}
                onClick={() => void handleSubmit()}
              >
                {isSubmitting ? (
                  <span className="feedback-popup-spinner-wrap">
                    <span className="spinner-login" />
                  </span>
                ) : (
                  'Send Feedback'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackPopup;
